import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { keyOverlayPath } from 'lib/paths'
import { AccountOverlayLink, AccountOverlayLinkKind } from 'types/api'
import ensureAccountOverlayLinkMutation from 'graphql/ensureAccountOverlayLink.graphql'

const LINK_KEY_SHAPE = /^[0-9A-Za-z]{26}$/

const ENSURE_FAILED = 'This overlay link could not be prepared. Try again.'
const REGENERATE_FAILED = 'A new link could not be made. The link shown is still the current one.'

export const buildKeyOverlayUrl = (
  k: string | null,
  origin: string = (typeof globalThis.location !== 'undefined' ? globalThis.location.origin : '')
): string | null => {
  if (k === null) return null
  return `${origin}${keyOverlayPath(k)}`
}

export interface AccountOverlayLinkState {
  url: string | null
  link: AccountOverlayLink | null
  busy: boolean
  error: string | null
  regenerate: () => Promise<boolean>
  retry: () => void
}

interface Slot {
  key: string | null
  link: AccountOverlayLink | null
  busy: boolean
  error: string | null
  regenerateError: string | null
}

const EMPTY_SLOT: Slot = { key: null, link: null, busy: false, error: null, regenerateError: null }

const slotKeyOf = (targetKind: AccountOverlayLinkKind, targetId: string): string =>
  `${targetKind}#${targetId}`

const isServedLinkFor = (
  link: unknown,
  targetKind: AccountOverlayLinkKind,
  targetId: string
): link is AccountOverlayLink => {
  if (typeof link !== 'object' || link === null) return false
  const row = link as Partial<AccountOverlayLink>
  return typeof row.k === 'string' &&
    LINK_KEY_SHAPE.test(row.k) &&
    row.targetKind === targetKind &&
    row.targetId === targetId
}

const requestLink = async (
  targetKind: AccountOverlayLinkKind,
  targetId: string,
  regenerate: boolean,
  auth0Token: string
): Promise<AccountOverlayLink> => {
  const { link } = await graphqlRequest<{ link: unknown }>(
    ensureAccountOverlayLinkMutation,
    { targetKind, targetId, ...(regenerate ? { regenerate: true } : {}) },
    auth0Token
  )
  if (!isServedLinkFor(link, targetKind, targetId)) throw new Error(ENSURE_FAILED)
  return link
}

const inFlightEnsures = new Map<string, Promise<AccountOverlayLink>>()

const ensureShared = async (
  targetKind: AccountOverlayLinkKind,
  targetId: string,
  auth0Token: string
): Promise<AccountOverlayLink> => {
  const key = slotKeyOf(targetKind, targetId)
  const pending = inFlightEnsures.get(key)
  if (pending !== undefined) return await pending
  const request = requestLink(targetKind, targetId, false, auth0Token)
    .finally(() => { inFlightEnsures.delete(key) })
  inFlightEnsures.set(key, request)
  return await request
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message !== '' ? err.message : fallback

export const useAccountOverlayLink = (
  targetKind: AccountOverlayLinkKind,
  targetId: string | null
): AccountOverlayLinkState => {
  const { auth0Token } = useAuth()
  const targetKey = targetId === null ? null : slotKeyOf(targetKind, targetId)
  const [slot, setSlot] = useState<Slot>(EMPTY_SLOT)
  const [attempt, setAttempt] = useState(0)
  const resolvedKeyRef = useRef<string | null>(null)
  const liveKeyRef = useRef<string | null>(targetKey)

  useEffect(() => {
    liveKeyRef.current = targetKey
    return () => {
      liveKeyRef.current = null
      resolvedKeyRef.current = null
      setSlot(EMPTY_SLOT)
    }
  }, [targetKey])

  useEffect(() => {
    if (targetId === null || auth0Token === undefined) return
    const key = slotKeyOf(targetKind, targetId)
    if (resolvedKeyRef.current === key) return
    let live = true
    setSlot((prev) => ({
      key,
      link: null,
      busy: true,
      error: null,
      regenerateError: prev.key === key ? prev.regenerateError : null
    }))
    ensureShared(targetKind, targetId, auth0Token)
      .then((link) => {
        if (!live) return
        resolvedKeyRef.current = key
        setSlot((prev) => ({
          key,
          link,
          busy: false,
          error: null,
          regenerateError: prev.key === key ? prev.regenerateError : null
        }))
      })
      .catch((err: unknown) => {
        if (!live) return
        setSlot((prev) => ({
          key,
          link: null,
          busy: false,
          error: messageOf(err, ENSURE_FAILED),
          regenerateError: prev.key === key ? prev.regenerateError : null
        }))
      })
    return () => { live = false }
  }, [targetKind, targetId, auth0Token, attempt])

  const regenerate = useCallback(async (): Promise<boolean> => {
    if (targetId === null || auth0Token === undefined) return false
    const key = slotKeyOf(targetKind, targetId)
    setSlot((prev) => prev.key === key
      ? { ...prev, busy: true, error: null, regenerateError: null }
      : prev)
    try {
      const link = await requestLink(targetKind, targetId, true, auth0Token)
      if (liveKeyRef.current !== key) return false
      resolvedKeyRef.current = key
      setSlot({ key, link, busy: false, error: null, regenerateError: null })
      return true
    } catch {
      if (liveKeyRef.current !== key) return false
      resolvedKeyRef.current = null
      setSlot({
        key,
        link: null,
        busy: false,
        error: null,
        regenerateError: REGENERATE_FAILED
      })
      setAttempt((n) => n + 1)
      return false
    }
  }, [targetKind, targetId, auth0Token])

  const retry = useCallback((): void => {
    if (resolvedKeyRef.current !== null && resolvedKeyRef.current === targetKey) return
    setAttempt((n) => n + 1)
  }, [targetKey])

  const current: Slot = slot.key === targetKey
    ? slot
    : { ...EMPTY_SLOT, key: targetKey, busy: targetKey !== null }
  const link = current.link
  return {
    url: buildKeyOverlayUrl(link === null ? null : link.k),
    link,
    busy: current.busy,
    error: current.error ?? current.regenerateError,
    regenerate,
    retry
  }
}
